/**
 * 首页地球/品牌字设置（PC / 手机端独立）
 *
 * 边距：单侧百分比。0 = 刚好铺满参照边；10 = 两侧各约 10% 留白（地球约占 80%）
 *   - 手机参照边 = 屏宽；PC 参照边 = 地图窗口高度
 * 文字倍率：1 = 与地球等宽；>1 允许宽出地球
 * 手机端倍率>1：文字铺满屏宽，地球按同比例缩小
 */

export const DEFAULT_DEVICE_HOMEPAGE = {
  brandText: '中国龙泉',
  brandFontSizeMode: 'auto',
  brandFontSize: 96,
  brandAutoScale: 1,
  brandColor: '#ffffff',
  brandOpacity: 0.48,
  brandLetterSpacing: 0.22,
  showSpinningEarthFirst: true,
  earthMarginPercent: 10
};

export const DEFAULT_HOMEPAGE_SETTINGS = {
  pc: {
    ...DEFAULT_DEVICE_HOMEPAGE,
    earthMarginPercent: 10
  },
  mobile: {
    ...DEFAULT_DEVICE_HOMEPAGE,
    brandLetterSpacing: 0.08,
    brandAutoScale: 1,
    earthMarginPercent: 0
  }
};

/**
 * Mapbox globe 像素标定：
 * 手机端约 390px 宽、zoom≈1.27 时刚好铺满
 * → diameter ≈ GLOBE_PIXEL_BASE * 2^zoom
 */
export const GLOBE_PIXEL_BASE = 162;

/**
 * 兼容旧版扁平结构，统一为 { pc, mobile }
 */
export function normalizeHomepageSettings(raw) {
  const base = raw && typeof raw === 'object' ? raw : {};

  if (base.pc || base.mobile) {
    return {
      pc: { ...DEFAULT_HOMEPAGE_SETTINGS.pc, ...(base.pc || {}) },
      mobile: { ...DEFAULT_HOMEPAGE_SETTINGS.mobile, ...(base.mobile || {}) }
    };
  }

  const legacyKeys = [
    'brandText',
    'brandFontSizeMode',
    'brandFontSize',
    'brandAutoScale',
    'brandColor',
    'brandOpacity',
    'brandLetterSpacing',
    'showSpinningEarthFirst',
    'earthZoomAdjust',
    'earthMarginPercent'
  ];
  const legacy = {};
  legacyKeys.forEach((key) => {
    if (base[key] !== undefined) legacy[key] = base[key];
  });

  if (legacy.earthMarginPercent == null && typeof legacy.earthZoomAdjust === 'number') {
    const mapped = 10 - legacy.earthZoomAdjust * 20;
    legacy.earthMarginPercent = Math.max(0, Math.min(35, mapped));
    delete legacy.earthZoomAdjust;
  } else {
    delete legacy.earthZoomAdjust;
  }

  return {
    pc: { ...DEFAULT_HOMEPAGE_SETTINGS.pc, ...legacy },
    mobile: { ...DEFAULT_HOMEPAGE_SETTINGS.mobile, ...legacy }
  };
}

export function pickHomepageDeviceSettings(settings, isMobileView) {
  const normalized = normalizeHomepageSettings(settings);
  return isMobileView ? normalized.mobile : normalized.pc;
}

export function isHomepageMobileView() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768;
}

/** 目标地球直径（像素）→ Mapbox zoom，PC/手机同一套绝对尺寸标定 */
export function zoomFromTargetDiameterPx(targetDiameterPx) {
  const d = Math.max(80, Number(targetDiameterPx) || 80);
  return Math.max(0.5, Math.min(3.5, Math.log2(d / GLOBE_PIXEL_BASE)));
}

/**
 * 按边距与文字倍率计算地球直径、文字目标宽度、zoom
 *
 * 边距 N：两侧各留约 N%（地球直径 ≈ 参照边 * (1 - 2N/100)）
 * 边距 0：直径 = 参照边（刚好铺满）
 */
export function getEarthFitMetrics(containerEl, deviceSettings, isMobileView) {
  const width = containerEl?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const height = containerEl?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 800);
  const defaultMargin = isMobileView ? 0 : 10;
  const marginPct = Math.max(
    0,
    Math.min(40, Number(deviceSettings?.earthMarginPercent ?? defaultMargin))
  ) / 100;
  const scale = Math.max(0.3, Number(deviceSettings?.brandAutoScale) || 1);

  // 单侧边距 N% → 两侧合计 2N%，地球占 (100-2N)%
  const fill = Math.max(0.2, Math.min(1, 1 - 2 * marginPct));

  let earthDiameter;
  let textWidth;
  let refSize;

  if (isMobileView) {
    refSize = width;
    const earthBase = width * fill;

    if (scale > 1) {
      // 文字宽出地球：文字铺满屏宽，地球同比缩小
      textWidth = width;
      earthDiameter = width / scale;
    } else {
      earthDiameter = earthBase;
      textWidth = earthBase * scale;
    }
  } else {
    // PC：以地图窗口高度为参照，0=铺满高度，10=上下各约10%
    refSize = height;
    earthDiameter = height * fill;
    textWidth = earthDiameter * scale;
  }

  earthDiameter = Math.max(80, earthDiameter);
  textWidth = Math.max(40, textWidth);

  const zoom = zoomFromTargetDiameterPx(earthDiameter);
  const visualFill = earthDiameter / refSize;

  return {
    zoom,
    diameter: earthDiameter,
    textWidth,
    width,
    height,
    marginPct,
    scale,
    refSize,
    visualFill
  };
}

/**
 * 按目标文字宽度算字号；倍率 1 = 与地球等宽
 */
export function calcBrandFontSizePx(deviceSettings, earthDiameter, textWidthOverride) {
  const text = String(deviceSettings?.brandText || '中国龙泉');
  const letter = Number(deviceSettings?.brandLetterSpacing);
  const letterEm = Number.isFinite(letter) ? letter : 0.22;
  const emWidth = text.length * (1.05 + letterEm);
  const scale = Math.max(0.3, Number(deviceSettings?.brandAutoScale) || 1);
  const targetTextWidth = textWidthOverride != null
    ? textWidthOverride
    : earthDiameter * scale;

  if (deviceSettings?.brandFontSizeMode === 'fixed' && Number(deviceSettings.brandFontSize) > 0) {
    return Math.round(Number(deviceSettings.brandFontSize));
  }

  const font = targetTextWidth / Math.max(emWidth, 1);
  return Math.round(Math.max(16, Math.min(240, font)));
}

export function hexToRgba(hex, opacity) {
  const raw = String(hex || '#ffffff').replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) {
    return `rgba(255,255,255,${opacity})`;
  }
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const a = Math.max(0, Math.min(1, Number(opacity) || 0));
  return `rgba(${r},${g},${b},${a})`;
}
